import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography, Divider} from 'views/components';
import {Translate, translate, Helmet} from 'views/module';

import backend from 'utils/backend';
import {simplifyQuestions} from 'utils/user_bio';

import HeaderSection from './sections/HeaderSection';
import CustomDescriptionSection from './sections/CustomDescriptionSection';
import ServicesSection from './sections/ServicesSection';
import CredentialsSection from './sections/CredentialsSection';
import ReviewSection from './sections/ReviewSection';
import WorkHistorySection from './sections/WorkHistorySection';
import CaregiverProfileSkeleton from './components/LoadingSkeleton';
import ProfileNotAvailable from './components/ProfileNotAvailable';


const CaregiverProfile = (props) => {
  const {
    caregiverRn,
    recommendationId,
    statusProgressWrap: progressWrap,
  } = props;

  const [caregiverData, setCaregiverData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const questionsBio = caregiverData?.caregiverBio?.questions || {};
  const questions = simplifyQuestions(questionsBio);
  const reviews = caregiverData?.caregiverReviews?.list || [];
  const customDescription = caregiverData?.caregiverRecommendationData?.recommendation?.blob;

  React.useEffect(() => {
    progressWrap(async () => {
      setLoading(true);
      const caregiverData = await backend.caregiver.getCaregiver(caregiverRn, recommendationId);
      setCaregiverData(caregiverData);
      setLoading(false);
    });
  }, [caregiverRn]);

  const containsMinData = React.useMemo(() => {
    const required = [
      'firstName',
      'lastName',
      'city',
      'province',
      'shortBio',
      'personalBio',
      'caregiverMotivation',
    ];
    return required.every((k) => questions?.[k]);
  }, [questions]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>
            {translate('caregiverProfile.metaData.CareQuilt')}
          </title>
        </Helmet>
        <CaregiverProfileSkeleton />
      </>
    );
  } else if (!containsMinData) {
    return (
      <>
        <Helmet>
          <title>
            {translate('caregiverProfile.metaData.NotFound')}
          </title>
        </Helmet>
        <ProfileNotAvailable />
      </>
    );
  }

  const {firstName, lastName, shortBio} = questions;
  const profilePhoto = questions.profilePhoto?.[0];

  return (
    <>
      <Helmet>
        <title>
          {translate('caregiverProfile.metaData.Profile.Title', {firstName, lastName})}
        </title>
        <meta
          property="og:title"
          content={translate('caregiverProfile.metaData.Profile.Title', {firstName, lastName})}
        />
        <meta
          name="description"
          content={translate('caregiverProfile.metaData.Profile.description', {firstName, lastName, shortBio})}
        />
        <meta
          property="og:description"
          content={translate('caregiverProfile.metaData.Profile.description', {firstName, lastName, shortBio})}
        />
        {
          profilePhoto && <meta property="og:image" content={profilePhoto} />
        }
      </Helmet>

      <Stack sx={{alignItems: 'center'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            m: {xs: 1, md: 2},
          }}
        >
          <Box
            component="img"
            src="/logo/quilt-icon.png"
            alt="CareQuilt Icon"
            sx={{
              width: {xs: 24, sm: 32, md: 40},
              height: {xs: 24, sm: 32, md: 40},
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: {xs: 24, sm: 32, md: 40},
            }}
          >
            <Translate expression='caregiverProfile.Main.CareQuilt'/>
          </Typography>
        </Box>

        <Divider
          sx={{
            borderColor: 'grey.500',
            borderBottomWidth: 2,
            width: '100%',
          }}
        />

        <Stack
          spacing={{xs: 3, sm: 4, md: 5, lg: 6}}
          sx={{
            alignItems: 'center',
            maxWidth: {sm: '100%', md: 700, lg: 1000},
            m: 2,
          }}
        >
          <HeaderSection questions={questions} />
          <CustomDescriptionSection
            customDescription={customDescription}
            questions={questions}/>
          <ReviewSection questions={questions} reviews={reviews} />
          <ServicesSection questions={questions} />
          <WorkHistorySection questions={questions} />
          <CredentialsSection questions={questions} />
        </Stack>
      </Stack>
    </>
  );
};

CaregiverProfile.propTypes = {
  caregiverRn: PropTypes.string.isRequired,
  recommendationId: PropTypes.string,
  statusProgressWrap: PropTypes.func.isRequired,
  caregiverReviews: PropTypes.object,
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverProfile));
